import React, {useState, useContext, useEffect} from 'react';
import {
  Alert, AlertTitle,
  Button, CircularProgress,
  InputAdornment, Snackbar,
  TextField,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { ApiContext } from '../../context/Api';
import QuestionHelp from './QuestionHelp';

const USER_INFO = 'userInfo';
const USER_ANSWERS = 'userAnswers';

function QuestionAnswer({ lessonId, block, answers }) {
  const [userInput, setUserInput] = useState('');
  const [inputIcon, setInputIcon] = useState('blank');
  const { t } = useTranslation();
  const { apiService } = useContext(ApiContext);
  const [showSuccess, setShowSuccess] = useState(false);
  const user = JSON.parse(localStorage.getItem(USER_INFO));
  const queryClient = useQueryClient();

  // region Get Answer
  async function getUserAnswerLocal() {
    const answerString = localStorage.getItem(USER_ANSWERS);
    if (!answerString) {
      return null;
    }

    try {
      const userAnswers = JSON.parse(answerString);
      if (!userAnswers) {
        return null;
      }

      const localAnswer = userAnswers.find((e) => e.lessonblock === block.id);

      if (!localAnswer) {
        return null;
      }

      return localAnswer;
    } catch (err) {
      Sentry.captureException(err);
      return null;
    }
  }
  async function getUserAnswerServer() {
    if (user && user.user.id && answers) {
      const userAnswer = answers.find(
        (a) =>
          (!a.user || a.user.id.toString() === user.user.id.toString()) &&
          (a.lessonblock.id === block.id ||
            (a.lessonQuestionId && a.lessonQuestionId.toString() === block.id)),
      );
      if (userAnswer) {
        return userAnswer;
      }
    }
    return null;
  }
  const { data: userAnswer } = useQuery({
    staleTime: 0,
    queryKey: ['userAnswer', lessonId, block.id, user?.user?.id],
    queryFn: async () => {
      const localAnswer = await getUserAnswerLocal();
      const serverAnswer = await getUserAnswerServer();
      const data = serverAnswer || localAnswer || {};
      return data;
    },
  });
  useEffect(() => {
    if (userAnswer?.answer && !userInput) {
      setUserInput(userAnswer.answer);
    }
  }, [userAnswer, userInput]);
  // endregion

  // region Save Answer
  async function saveAnswerLocal(answerToSave) {
    const lessonBlockId = block.id;
    const newAnswer = {
      lessonId,
      lessonblock: lessonBlockId,
      answer: answerToSave,
    };

    // get current answers from storage
    try {
      const userAnswers = JSON.parse(localStorage.getItem(USER_ANSWERS));

      if (!userAnswers) {
        // if no exising answers, just save this one
        await localStorage.setItem(USER_ANSWERS, JSON.stringify([newAnswer]));
      } else {
        // if existing local answers, merge this answer into it
        const exists = userAnswers.find((e) => e.lessonblock === lessonBlockId);
        if (exists) {
          exists.answer = answerToSave;
        } else {
          userAnswers.push(newAnswer);
        }
        localStorage.setItem(USER_ANSWERS, JSON.stringify(userAnswers));
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  }
  async function saveAnswerServer(answerToSave) {
    if (!user) {
      return null;
    }

    let answerId = userAnswer?.id;
    const result = await apiService.saveUserAnswer(
      user,
      lessonId,
      block.id,
      userAnswer?.id,
      answerToSave,
    );
    if (result && result.data && (result.data.createAnswer || result.data.updateAnswer)) {
      answerId =
        result.data?.createAnswer?.answer?.id ??
        result.data?.createAnswer?.data?.id ??
        result.data?.updateAnswer?.answer?.id ??
        result.data?.updateAnswer?.data?.id;
    }
    return answerId;
  }
  const saveUserAnswer = useMutation({
    mutationFn: () => {
      setInputIcon('loading');
      saveAnswerLocal(userInput);

      return saveAnswerServer(userInput);
    },
    onSuccess: (answerId) => {
      // openToast();
      setShowSuccess(true);
      setInputIcon('check');
      setTimeout(() => {
        setInputIcon('blank');
      }, 4000);

      // invalidate the userAnswers query so it will perform the query again
      queryClient.invalidateQueries(['getUserAnswers', user?.user?.id]);

      // update the data of the userAnswer query without making it perform the query again
      queryClient.setQueryData(['userAnswer', lessonId, block.id, user?.user?.id], {
        ...userAnswer,
        id: answerId,
        answer: userInput,
      });
    },
    onError: (error) => {
      console.error(error);
      Sentry.captureException(error);
      setInputIcon('check');
    },
  });
  // endregion

  let rightIcon = null;
  if (inputIcon === 'loading') {
    rightIcon = <InputAdornment position="end"><CircularProgress size="1.5rem" /></InputAdornment>;
  } else if (inputIcon === 'check') {
  rightIcon = <InputAdornment position="end"><Check /></InputAdornment>;
  }

  return (
    <div className="question-answer">
      <TextField
        label={t('lesson.question.yourAnswer')}
        placeholder={t('lesson.question.enterYourAnswer')}
        variant="filled"
        value={userInput}
        onChange={e => setUserInput(e.target.value)}
        sx={{ width: '100%' }}
        slotProps={{
          input: {
            endAdornment: rightIcon,
          },
        }}
      />
      <div className="action-row">
        <Button
          variant="contained"
          onClick={() => saveUserAnswer.mutate()}
          disabled={saveUserAnswer.isPending}
        >{t('core.save')}</Button>

        <QuestionHelp block={block} />
      </div>
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          <AlertTitle>{t('lesson.question.answer')}</AlertTitle>
          {t('lesson.question.answerSaved')}
        </Alert>
      </Snackbar>
    </div>
  )
}
export default QuestionAnswer;
